/**
 * OppmoteEditor - MVP version (uten talent-kobling)
 * Viser og redigerer oppmøtetider for en produksjon
 */

import React, { useState } from 'react';
import { Clock, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import SectionTitle from './SectionTitle';
import { useOppmoteData } from '@/hooks/useOppmoteData';
import { useOppmoteTalents } from './oppmote-editor/hooks/useOppmoteTalents';
import { useOppmoteForm } from './oppmote-editor/hooks/useOppmoteForm';
import { useOppmoteSubmit } from './oppmote-editor/hooks/useOppmoteSubmit';
import { OppmoteTable } from './oppmote-editor/components/OppmoteTable';
import { OppmoteEmptyState } from './oppmote-editor/components/OppmoteEmptyState';
import { OppmoteDialog } from './oppmote-editor/components/OppmoteDialog';

interface OppmoteEditorProps {
  produksjonId: number;
  produksjonTid: string; // ISO 8601 timestamp
  readOnly?: boolean;
}

const OppmoteEditor: React.FC<OppmoteEditorProps> = ({ 
  produksjonId, 
  produksjonTid,
  readOnly = false 
}) => {
  const {
    oppmote,
    loading,
    error: oppmoteQueryError,
    removeOppmote: removeOppmoteMutation,
    refresh,
  } = useOppmoteData(produksjonId);
  const { allTalents, allKategorier } = useOppmoteTalents();
  const {
    formData,
    setFormData,
    editingId,
    formError,
    setFormError,
    openDialog,
    closeDialog,
    resetForm,
  } = useOppmoteForm({ oppmote, allTalents, allKategorier });
  const { handleSubmit: submitOppmote } = useOppmoteSubmit({
    produksjonId,
    produksjonTid,
    oppmote,
  });
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleOpenDialog = (item?: any) => {
    openDialog(item);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    closeDialog();
    resetForm();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const success = await submitOppmote(formData, editingId, setFormError);
    if (success) {
      handleCloseDialog();
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Er du sikker på at du vil slette denne oppmøtetiden?')) {
      return;
    }

    try {
      await removeOppmoteMutation(id);
      await refresh();
    } catch (err) {
      console.error('Feil ved sletting av oppmøtetid:', err);
      toast.error('Kunne ikke slette oppmøtetid');
    }
  };

  if (loading) {
    return <div className="text-center py-8 text-muted-foreground">Laster oppmøtetider...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <SectionTitle icon={Clock}>Oppmøtetider</SectionTitle>
          <p className="text-sm text-muted-foreground">
            Administrer oppmøtetider for denne produksjonen
          </p>
        </div>
        {!readOnly && (
          <Button onClick={() => handleOpenDialog()} size="sm">
            <Plus className="h-4 w-4 mr-2" />
            Legg til oppmøtetid
          </Button>
        )}
      </div>
      {oppmoteQueryError && (
        <div className="text-sm text-destructive">
          Kunne ikke hente oppmøtetider. Prøv å laste inn siden på nytt.
        </div>
      )}

      {oppmote.length === 0 ? (
        <OppmoteEmptyState
          readOnly={readOnly}
          onAdd={() => handleOpenDialog()}
        />
      ) : (
        <OppmoteTable
          oppmote={oppmote}
          readOnly={readOnly}
          onEdit={(item) => handleOpenDialog(item)}
          onDelete={handleDelete}
        />
      )}

      <OppmoteDialog
        open={dialogOpen}
        editingId={editingId}
        formData={formData}
        formError={formError}
        onClose={handleCloseDialog}
        onFormDataChange={(data) => setFormData({ ...formData, ...data })}
        onSubmit={handleSubmit}
      />
    </div>
  );
};

export default OppmoteEditor;

